import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, Building2, Users, Mic, IndianRupee, ExternalLink, AlertCircle } from 'lucide-react';
import api from '../services/api';

/**
 * /admin → Analytics tab. super_admin only.
 *
 * Two halves:
 *   Top: native MongoDB-backed metrics (orgs, users, interview minutes,
 *        revenue, trend chart, top orgs, recent signups). These render even
 *        if Plausible isn't configured.
 *   Bottom: embedded Plausible dashboard for visitor traffic — falls back
 *        to a setup-instructions card if not configured.
 */

const RANGES = [
  { d: 7, label: '7 days' },
  { d: 30, label: '30 days' },
  { d: 90, label: '90 days' },
];

function Card({ icon: Icon, label, value, delta, unit }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-ink-900 p-5">
      <div className="flex items-start justify-between">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand-500/15 text-brand-400">
          <Icon size={18} />
        </div>
        {delta != null && (
          <span className={`text-xs ${delta > 0 ? 'text-emerald-400' : 'text-ink-500'}`}>
            {delta > 0 ? '+' : ''}{delta} new
          </span>
        )}
      </div>
      <div className="mt-4 text-3xl font-extrabold text-white">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wide text-ink-400">{label}{unit && <span className="ml-1 normal-case text-ink-500">{unit}</span>}</div>
    </div>
  );
}

function TrendChart({ series }) {
  // Lightweight SVG dual-line chart: signups_orgs + signups_users per day.
  // No external chart lib — keeps the bundle small and the look on-brand.
  const W = 800, H = 220, P = 28;
  if (!series?.length) return <div className="text-sm text-ink-400">No data yet.</div>;

  const xs = series.map((_, i) => i);
  const orgsY = series.map(s => s.signups_orgs);
  const usersY = series.map(s => s.signups_users);
  const maxY = Math.max(1, ...orgsY, ...usersY);

  const px = (i) => P + (i / Math.max(1, series.length - 1)) * (W - 2 * P);
  const py = (y) => H - P - (y / maxY) * (H - 2 * P);

  const pathFor = (arr) => arr.map((y, i) => `${i === 0 ? 'M' : 'L'} ${px(i).toFixed(1)} ${py(y).toFixed(1)}`).join(' ');

  return (
    <div className="rounded-2xl border border-white/10 bg-ink-900 p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Daily signups</h3>
        <div className="flex items-center gap-3 text-xs text-ink-400">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-brand-400" /> Orgs</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Users</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
        {/* Y-axis grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
          <line key={i} x1={P} x2={W - P} y1={P + p * (H - 2 * P)} y2={P + p * (H - 2 * P)} stroke="#27272a" strokeDasharray="2 3" />
        ))}
        <path d={pathFor(orgsY)}  fill="none" stroke="#d4af37" strokeWidth="2" />
        <path d={pathFor(usersY)} fill="none" stroke="#34d399" strokeWidth="2" />
        {/* x-axis: a few date labels */}
        {[0, Math.floor(series.length / 2), series.length - 1].map((i) => (
          <text key={i} x={px(i)} y={H - 8} fontSize="10" fill="#71717a" textAnchor="middle">
            {series[i]?.date?.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  );
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [series, setSeries]     = useState([]);
  const [embed, setEmbed]       = useState({ configured: false, embed_url: '', setup_hint: '' });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  useEffect(() => {
    let stale = false;
    setLoading(true);
    setError('');
    Promise.all([
      api.get('/analytics/overview'),
      api.get(`/analytics/timeseries?days=${days}`),
      api.get('/analytics/plausible-embed').catch(() => ({ data: { configured: false } })),
    ]).then(([o, t, p]) => {
      if (stale) return;
      setOverview(o.data);
      setSeries(t.data?.series || []);
      setEmbed(p.data || { configured: false });
    }).catch((e) => {
      if (stale) return;
      setError(e.response?.data?.detail || 'Could not load analytics.');
    }).finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [days]);

  const totals = overview?.totals || {};
  const d30 = overview?.delta_30d || {};
  const topOrgs = overview?.top_orgs_by_usage || [];
  const recent  = overview?.recent_signups || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-extrabold text-white">Analytics</h2>
          <p className="mt-1 text-sm text-ink-400">
            Platform-wide footprint — signups, usage, revenue, and visitor traffic.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-xl border border-white/10 bg-ink-900 p-1">
          {RANGES.map((r) => (
            <button
              key={r.d}
              onClick={() => setDays(r.d)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                days === r.d ? 'bg-brand-500 text-ink-950' : 'text-ink-400 hover:bg-white/5 hover:text-white'
              }`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {loading && !overview ? (
        <div className="rounded-xl border border-white/10 bg-ink-900 p-8 text-center text-ink-400">Loading…</div>
      ) : (
        <>
          {/* Counter row */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Card icon={Building2} label="Organizations" value={totals.organizations ?? '—'} delta={d30.organizations} />
            <Card icon={Users}     label="Users"         value={totals.users ?? '—'}         delta={d30.users} />
            <Card icon={Mic}       label="Credits consumed" value={totals.interview_credits_consumed ?? '—'} unit="(≈ interview minutes)" />
            <Card icon={IndianRupee} label="Revenue (paid)"   value={`₹${Number(totals.revenue_inr_paid ?? 0).toLocaleString('en-IN')}`} delta={d30.revenue_inr_paid ? `+₹${Number(d30.revenue_inr_paid).toLocaleString('en-IN')}` : 0} />
          </div>

          {/* Trend chart */}
          <TrendChart series={series} />

          {/* Two-column: Top orgs + Recent signups */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-ink-900 p-5">
              <h3 className="mb-3 text-sm font-semibold text-white">Top orgs by usage</h3>
              {topOrgs.length === 0 ? (
                <p className="text-sm text-ink-400">No usage yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-ink-500">
                      <th className="text-left">Organization</th>
                      <th className="text-right">Credits used</th>
                      <th className="text-right">Quota</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {topOrgs.map((o) => (
                      <tr key={o.organization_id}>
                        <td className="py-2 text-white">{o.organizationName || o.organization_id}</td>
                        <td className="py-2 text-right text-ink-300">{o.interviews_used ?? 0}</td>
                        <td className="py-2 text-right text-ink-500">{o.interview_quota ?? '∞'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-ink-900 p-5">
              <h3 className="mb-3 text-sm font-semibold text-white">Recent signups</h3>
              {recent.length === 0 ? (
                <p className="text-sm text-ink-400">No signups yet.</p>
              ) : (
                <ul className="divide-y divide-white/5">
                  {recent.slice(0, 8).map((o) => (
                    <li key={o.organization_id} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-white">{o.organizationName}</span>
                      <span className="text-xs text-ink-500">
                        {o.industry || '—'} · {o.created_at ? new Date(o.created_at).toLocaleDateString() : '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Plausible iframe (or setup callout) */}
          <div className="rounded-2xl border border-white/10 bg-ink-900 p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Visitor traffic (Plausible)</h3>
              {embed.configured && (
                <a href={embed.embed_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-400 hover:underline">
                  Open in Plausible <ExternalLink size={12} />
                </a>
              )}
            </div>
            {embed.configured ? (
              <iframe
                title="Plausible — aietafirst.com"
                src={embed.embed_url}
                loading="lazy"
                className="h-[600px] w-full rounded-xl border border-white/10 bg-white"
              />
            ) : (
              <div className="rounded-xl border border-dashed border-white/15 bg-ink-950 p-6 text-sm text-ink-300">
                <p className="mb-2 font-semibold text-white">Plausible isn't connected yet.</p>
                <ol className="ml-4 list-decimal space-y-1 text-ink-400">
                  <li>Sign up at <code>plausible.io</code> and add <code>aietafirst.com</code> as a site (or self-host).</li>
                  <li>Site settings → Shared dashboards → generate a shareable link.</li>
                  <li>
                    Set <code>PLAUSIBLE_DASHBOARD_URL</code> on the admin-backend service (env var)
                    and restart the container. This page will embed it here.
                  </li>
                </ol>
                <p className="mt-3 text-xs text-ink-500">
                  The Plausible <em>script</em> is already live on the public site — visits start
                  tracking the moment you add the site at plausible.io. This embed step is only
                  for showing the dashboard inside this admin tab.
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
